import { useEffect, useRef, useState } from 'react';
import jwtAxios from '@nxsys/services/axios';
import { socketYearEndProcess } from '@nxsys/services/sockets';
import KickOffCheckImg from '@nxsys/assets/images/kick-off/kick-off-check.svg';
import KickOffKeyboardImg from '@nxsys/assets/images/kick-off/kick-off-keyboard.svg';
import KickOffPaperImg from '@nxsys/assets/images/kick-off/kick-off-paper.svg';
import KickOffRocket1Img from '@nxsys/assets/images/kick-off/kick-off-rocket_1.svg';

interface YearEndProcess {
  taxYear: number;
  status: 'PROCESSING' | 'COMPLETED';
  progress: number;
  processingMessage?: string;
  p60Date?: string;
  totalSubcontractors: number;
  sentP60Count: number;
  totalP60NeedToSend: number;
}

interface SummaryStepProps {
  yearEndProcess: YearEndProcess | null;
  companyParentId?: string;
}

const SummaryStep = ({ yearEndProcess: yearEndProcessProp, companyParentId }: SummaryStepProps) => {
  const [yearEndProcess, setYearEndProcess] = useState<YearEndProcess | null>(
    yearEndProcessProp,
  );
  const [isProcessing, setIsProcessing] = useState(
    yearEndProcessProp?.status === 'PROCESSING',
  );
  const [percentageProcess, setPercentageProcess] = useState(
    Math.round((yearEndProcessProp?.progress || 0) * 100),
  );
  const barRef = useRef<HTMLDivElement>(null);

  // Sync from parent prop when it changes
  useEffect(() => {
    if (yearEndProcessProp) {
      setYearEndProcess(yearEndProcessProp);
      setPercentageProcess(
        Math.round((yearEndProcessProp.progress || 0) * 100),
      );
    }
  }, [yearEndProcessProp]);

  // Poll API for progress (worker socket emissions don't reach frontend)
  useEffect(() => {
    if (!isProcessing) return;
    const interval = setInterval(async () => {
      try {
        const res = await jwtAxios.get('/company-parent/current-year-end-process');
        const data = res?.data as YearEndProcess;
        if (!data) return;
        setYearEndProcess(data);
        setPercentageProcess(Math.round((data.progress || 0) * 100));
        if (data.progress >= 1 || data.status === 'COMPLETED') {
          clearInterval(interval);
          setTimeout(() => setIsProcessing(false), 1000);
        }
      } catch {
        // Silently ignore polling errors
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [isProcessing]);

  // Socket listener (backup — currently not working due to worker architecture)
  useEffect(() => {
    if (!companyParentId) return;
    const channel = `year-end-process-${companyParentId}`;
    const handler = (res: any) => {
      const data = res?.data as YearEndProcess;
      if (!data) return;
      setYearEndProcess(data);
      setPercentageProcess(Math.round((data.progress || 0) * 100));
      if (data.progress >= 1) {
        setTimeout(() => setIsProcessing(false), 1000);
      } else {
        setIsProcessing(data.progress < 1);
      }
    };
    socketYearEndProcess.on(channel, handler);
    return () => {
      socketYearEndProcess.off(channel, handler);
    };
  }, [companyParentId]);

  // Update isProcessing from state
  useEffect(() => {
    if (yearEndProcess?.progress !== undefined && yearEndProcess.progress >= 1) {
      const timer = setTimeout(() => setIsProcessing(false), 1000);
      return () => clearTimeout(timer);
    }
    setIsProcessing(
      yearEndProcess?.status === 'PROCESSING' ||
        (yearEndProcess?.progress !== undefined && yearEndProcess.progress < 1),
    );
  }, [yearEndProcess?.progress, yearEndProcess?.status]);

  // Animate progress bar
  useEffect(() => {
    const el = barRef.current;
    if (!el || percentageProcess === 0) return;
    let width = parseInt(el.style.width?.split('%')[0] || '0');
    const interval = setInterval(() => {
      width++;
      el.style.width = `${width}%`;
      if (width >= percentageProcess) clearInterval(interval);
    }, 10);
    return () => clearInterval(interval);
  }, [percentageProcess]);

  if (isProcessing) {
    return (
      <div>
        <div
          style={{
            color: '#00795B',
            fontSize: '1.3rem',
            textAlign: 'center',
            marginBottom: 8,
          }}
        >
          Processing your data. Please wait...
        </div>
        <div
          style={{
            width: 200,
            margin: 'auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <div
            style={{
              flexGrow: 1,
              backgroundColor: '#f0f0f0',
              borderRadius: 5,
            }}
          >
            <div
              ref={barRef}
              style={{
                height: 6,
                backgroundColor: '#00795B',
                width: '0%',
                borderRadius: 5,
                transition: 'width 0.1s linear',
              }}
            />
          </div>
          <div style={{ width: 40 }}>{percentageProcess}%</div>
        </div>
        {yearEndProcess?.processingMessage && (
          <div
            style={{
              color: 'gray',
              fontSize: '0.9rem',
              textAlign: 'center',
              marginTop: 8,
            }}
          >
            {yearEndProcess.processingMessage}
          </div>
        )}
      </div>
    );
  }

  const taxYear = yearEndProcess?.taxYear || 0;
  const newTaxYear = `${taxYear + 1}-${taxYear + 2}`;

  const completionCards = [
    {
      key: 'ytd',
      title: yearEndProcess?.totalSubcontractors,
      description: 'Employee YTD balances have been updated',
      icon: <img src={KickOffCheckImg} alt="check" />,
    },
    {
      key: 'rates',
      title: 'Rates & thresholds',
      description: `Have now been updated for the ${newTaxYear} tax year`,
      icon: <img src={KickOffRocket1Img} alt="rocket" />,
    },
    {
      key: 'fps',
      title: 'Final FPS & EPS',
      description: 'Have been submitted to HMRC',
      icon: <img src={KickOffPaperImg} alt="paper" />,
    },
    {
      key: 'p60',
      title: 'P60s',
      description: yearEndProcess?.p60Date
        ? 'Will be sent as scheduled'
        : `Sent ${yearEndProcess?.sentP60Count}/${yearEndProcess?.totalP60NeedToSend}`,
      icon: <img src={KickOffKeyboardImg} alt="keyboard" />,
    },
  ];

  return (
    <>
      <div
        style={{
          color: '#00795B',
          fontSize: '1.3rem',
          textAlign: 'center',
          marginBottom: 8,
        }}
      >
        🎉 That&apos;s a wrap! Your payroll is all set for the new tax
        year—smooth sailing ahead! ✅💼🚀
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          rowGap: 24,
          columnGap: 24,
          padding: '12px 0',
        }}
      >
        {completionCards.map((item) => (
          <div
            key={item.key}
            style={{
              padding: 16,
              border: '1px solid #00795B',
              borderRadius: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {item.icon}
            <div style={{ fontWeight: 'bold' }}>{item.title}</div>
            <div style={{ textAlign: 'center', fontSize: '0.85rem' }}>
              {item.description}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default SummaryStep;
